import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'

import useRouter from '../../hooks/useRouter'
import useSkyProducts from '../../hooks/socialdash/useSkyProducts'

import {
  ADMIN_SLUG,
  ADMIN_TAB,
  DEFAULT_SKY_PRODUCT,
  RECONCILIATION_TAB,
  orderProductTabs,
  productTabParams,
  spendLandingProduct,
  withAdminTab,
  selectedTab,
  withReconciliationTab,
} from './productTabsModel'

// The tab rules live in productTabsModel.ts, so a test (and routes.tsx) can
// read them without importing MUI; they are re-exported here for the pages.
export {
  ADMIN_SLUG,
  ADMIN_TAB,
  DEFAULT_SKY_PRODUCT,
  RECONCILIATION_TAB,
  orderProductTabs,
  productTabParams,
  spendLandingProduct,
  withAdminTab,
  selectedTab,
  withReconciliationTab,
}

interface ProductTabsProps {
  /** The active slug. Falsy (the bare `/sky-social-media` URL) selects the default. */
  product?: string
}

/**
 * The department tab strip that sits above every product dashboard.
 *
 * Replaces the five-card chooser page (Matt, 2026-08-17): switching department
 * was a two-click round trip through a page that carried nothing else, so the
 * chooser's whole job — "which department?" — becomes a control on the
 * dashboards themselves and Social Media opens straight onto Brand.
 *
 * 🔴 It renders on the DASHBOARDS, never on the spend page. Token spend is a
 * different question about the same department, so it sits outside the tab set
 * and switches department with its own dropdown; a tab strip there would claim
 * the spend page is one of the six dashboards.
 *
 * Nothing renders until the catalogue arrives, and nothing renders for a single
 * department: a one-tab strip is chrome that answers no question.
 */
export const ProductTabs: React.FC<ProductTabsProps> = ({ product }) => {
  const router = useRouter()
  const { products, isLoading } = useSkyProducts()

  const tabs = withReconciliationTab(withAdminTab(orderProductTabs(products)))
  const active = (product || DEFAULT_SKY_PRODUCT).trim().toLowerCase()

  if (isLoading || tabs.length < 2) return null

  // An unknown slug (a mistyped URL) selects nothing rather than throwing MUI's
  // out-of-range warning and arbitrarily highlighting the first tab — the page
  // below is already saying the department does not exist. Reconciliation
  // selects Admin for an admin, who opens it under that tab.
  const value = selectedTab(tabs, active)

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
      <Tabs
        value={value}
        onChange={(_, slug: string) =>
          slug === ADMIN_TAB
            ? router.navigate('sky-social-media-entities')
            : slug === RECONCILIATION_TAB
              ? router.navigate('sky-social-media-reconciliation')
              : router.navigate('sky-social-media-product', productTabParams(slug))
        }
        variant="scrollable"
        scrollButtons="auto"
        // MUI's textColor/indicatorColor only accept primary/secondary/inherit,
        // so the house info blue (see CLAUDE.md's style rule) is applied through
        // sx rather than the props.
        sx={{
          '& .MuiTabs-indicator': { bgcolor: 'info.main' },
          '& .MuiTab-root.Mui-selected': { color: 'info.main' },
        }}
        data-testid="sky-product-tabs"
      >
        {tabs.map((p) => (
          <Tab
            key={p.slug}
            value={p.slug}
            label={p.name}
            // The top bar has its own "Admin" button, earlier in the page; a
            // walkthrough (help/walkthroughs/sky-team-dashboards.ts) finds
            // controls by name, so this tab says whose admin it is.
            aria-label={p.slug === ADMIN_TAB ? 'Social media admin' : undefined}
            data-testid={`sky-product-tab-${p.slug}`}
          />
        ))}
      </Tabs>
    </Box>
  )
}

export default ProductTabs
